/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/jsx-curly-spacing */
import './LandingPage.css';

import { useDetectScroll } from '@saviynt/common';
import {
  Badge,
  ButtonSelect,
  Header,
  Icon,
  InputField,
  Link,
  ListRow,
  MenuMulti,
  PaginationCondensed,
  PaginationGroup,
} from '@saviynt/design-system';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { listAPIListReceived } from '../../action/PrivilegedAccessAction';
import LandingPageSidePanel from '../../components/LandingPageSidePanel/LandingPageSidePanel';
// TODO: remove mock data after merging and QA testing
import { mockData } from '../../models/MockData/MockData';
import LandingPageFilterService from '../../services/LandingPageFilterService';
import LandingPagePaginationService from '../../services/LandingPagePaginationService';
import {
  getListRowApi,
  getListRowApiFilterAndSearch,
} from '../../utilities/api/getDataFromApi';
import debounce from '../../utilities/debounce';
import useCreateRowObject from '../../utilities/useCreateRowObject';

function LandingPage() {
  const [currentListItem, setCurrentListItem] = useState(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [listRowData, setListRowData] = useState({});
  const [filterPillVisibility, setFilterPillVisibility] = useState(true);
  // Filter / Search
  const [searchBarValue, setSearchBarValue] = useState('');
  const [filteredEntities, setFilteredEntities] = useState(null);
  // References
  const landingPageContentRef = useRef(null);
  const history = useHistory();
  // todo once beta version ready, remove this constant as well.
  const ENABLE_FOR_BETA = false;
  // Filter Dependencies //
  const {
    platform,
    asset,
    isFilterChecked,
    clearAllFilters,
    checkedPlatforms,
    checkedAssets,
  } = LandingPageFilterService(setIsSidePanelOpen, setCurrentListItem);

  // Pagination Dependencies //
  const {
    pageCount,
    pageIndex,
    setPageIndex,
    pagRange,
    setPagRange,
    resArrayLength,
    paginatedEntities,
  } = LandingPagePaginationService(filteredEntities);

  const contentClasses = classnames(
    'LandingPage-content',
    isSidePanelOpen && 'LandingPage-content--withSidePanel'
  );

  const titleContainerClasses = classnames(
    'LandingPage-titleContainer',
    isSidePanelOpen && 'LandingPage-titleContainer--withSidePanel'
  );

  const listRowContainerClasses = classnames(
    'LandingPage-listRows',
    isSidePanelOpen && 'LandingPage-listRows--withSidePanel'
  );

  const rowIsSelected = (listItem) => currentListItem === listItem;

  const listRowOnClick = (row) => {
    setIsSidePanelOpen(true);
    setCurrentListItem(row);
  };

  const sidePanelOnClose = () => {
    setIsSidePanelOpen(false);
    setCurrentListItem(null);
  };

  useEffect(() => {
    // Close the sidepanel when page is changed or search made
    setIsSidePanelOpen(false);
    setCurrentListItem(null);
  }, [pageIndex, searchBarValue]);

  const { isScrolled: landingPageContentIsScrolled } = useDetectScroll(
    null,
    landingPageContentRef
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (listRowData && Object.keys(listRowData).length === 0) return;

    getListRowApiFilterAndSearch(
      checkedPlatforms,
      checkedAssets,
      searchBarValue
    ).then((response) => {
      setListRowData(response);
    });
  }, [checkedPlatforms, checkedAssets]);

  const delayedSearch = useRef(
    debounce((platforms, assets, query) => {
      if (query.trim().length > 0) {
        getListRowApiFilterAndSearch(platforms, assets, query).then(
          (response) => {
            setListRowData(response);
          }
        );
      }
    }, 300)
  ).current;

  useEffect(() => {
    // TODO: remove mock data after merging and QA testing
    const shouldUseMockData = window.location.href === 'http://localhost:3001/';

    if (shouldUseMockData) {
      console.log('Using mock data on localhost');
      setListRowData(mockData);
    }

    document.body.style.overflow = 'hidden';
    delayedSearch.cancel();

    return () => {
      document.body.style.overflow = 'auto';
      delayedSearch.cancel();
    };
  }, []);

  useEffect(() => {
    if (listRowData && Object.keys(listRowData).length !== 0) {
      setFilteredEntities(listRowData);
    }

    // To get data stored in redux store on getting response from API
    if (listRowData && Object.keys(listRowData).length > 0) {
      dispatch(listAPIListReceived(listRowData, 'success'));
    }
  }, [listRowData]);

  // Navigation functionality
  const checkoutCredentialNavigation = () => {
    history.push('/pam/check-out-credential');
  };

  // Search functionality
  useEffect(() => {
    if (searchBarValue.trim().length > 0) {
      delayedSearch(checkedPlatforms, checkedAssets, searchBarValue);
    } else if (checkedPlatforms.length > 0 || checkedAssets.length > 0) {
      getListRowApiFilterAndSearch(
        checkedPlatforms,
        checkedAssets,
        searchBarValue
      ).then((response) => {
        setListRowData(response);
      });
    } else {
      delayedSearch.cancel();
      getListRowApi().then((response) => {
        setListRowData(response);
      });
    }
  }, [searchBarValue]);

  const renderListOrNoData = () => {
    if (!paginatedEntities) {
      return null;
    }

    if (paginatedEntities.length === 0) {
      return (
        <div className='LandingPage-noData'>
          <Icon kind='alertCriticalOutline' />
          <span className='LandingPage-noData-title'>
            Your search doesn&apos;t match any records
          </span>
        </div>
      );
    }

    return (
      <div>
        {paginatedEntities.filter((item) => item.type === 'session').length >
          0 && (
          <div className='LandingPage-listrow-section-title LandingPage-listrow-section-title-livesession'>
            <div className='LandingPage-listrow-section-title-startLine' />
            <div className='LandingPage-listrow-section-title-label'>
              Live sessions
            </div>
            <div className='LandingPage-listrow-section-title-endLine' />
          </div>
        )}
        <div className='LandingPage-listrow-section'>
          {paginatedEntities
            .filter((item) => item.type === 'session')
            .map((listItemObj, i) => {
              const createObjectForRow = useCreateRowObject(listItemObj);
              const listItem = createObjectForRow();

              return (
                <div
                  key={listItem.requestAccessKey}
                  className={
                    rowIsSelected(listItemObj)
                      ? 'LandingPage-listRowContainer--selected'
                      : 'LandingPage-listRowContainer'
                  }>
                  <ListRow
                    category={listItem.category}
                    title={listItem.title}
                    description={listItem.description}
                    status={
                      !rowIsSelected(listItemObj) && listItem.status
                    }
                    primaryIcon={!rowIsSelected(listItemObj) && listItem.primaryIcon}
                    primaryIconOnClick={() => {
                      checkoutCredentialNavigation();
                    }}
                    secondaryIcon={ENABLE_FOR_BETA && !rowIsSelected(listItemObj) && listItem.secondaryIcon}
                    hasMoreOptions={!rowIsSelected(listItemObj) && listItem.hasMoreOptions}
                    onRowClick={() => listRowOnClick(listItemObj)}
                    className={
                      rowIsSelected(listItemObj)
                        ? 'LandingPage-listRow--selected'
                        : 'LandingPage-listRow'
                    }
                    type={listItem.type}
                    dataTestId={`session-row-${i}`}
                  />
                </div>
              );
            })}
        </div>

        {paginatedEntities.filter((item) => item.type === 'endpoint').length >
          0 && (
          <div className='LandingPage-listrow-section-title LandingPage-listrow-section-title-asset'>
            <div className='LandingPage-listrow-section-title-startLine' />
            <div className='LandingPage-listrow-section-title-label'>
              Available Assets
            </div>
            <div className='LandingPage-listrow-section-title-endLine' />
          </div>
        )}
        <div className='LandingPage-listrow-section'>
          {paginatedEntities
            .filter((item) => item.type === 'endpoint')
            .map((listItemObj, i) => {
              const createObjectForRow = useCreateRowObject(listItemObj);
              const listItem = createObjectForRow();

              return (
                <div
                  key={listItem.endpointKey}
                  className={
                    rowIsSelected(listItemObj)
                      ? 'LandingPage-listRowContainer--selected'
                      : 'LandingPage-listRowContainer'
                  }>
                  <ListRow
                    category={listItem.category}
                    title={listItem.title}
                    description={listItem.description}
                    status={listItem.status}
                    primaryIcon={ENABLE_FOR_BETA && listItem.primaryIcon}
                    primaryIconOnClick={checkoutCredentialNavigation}
                    secondaryIcon={ENABLE_FOR_BETA && listItem.secondaryIcon}
                    hasMoreOptions={listItem.hasMoreOptions}
                    onRowClick={() => listRowOnClick(listItemObj)}
                    className={
                      rowIsSelected(listItemObj)
                        ? 'LandingPage-listRow--selected'
                        : 'LandingPage-listRow'
                    }
                    type={listItem.type}
                    dataTestId={`endpoint-row-${i}`}
                  />
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <div className='LandingPage' data-testid='LandingPage'>
      <Header
        helpOnClick={() => {}}
        notificationsOnClick={() => {}}
        avatarOnClick={() => {}}
        user={{
          name: 'Test User',
          url: 'https://www.vhv.rs/dpng/d/15-155087_dummy-image-of-user-hd-png-download.png',
        }}
        isScrolled={landingPageContentIsScrolled}
        className='LandingPage-header'
      />
      <div className='LandingPage-layout'>
        <section ref={landingPageContentRef} className={contentClasses}>
          <section className={titleContainerClasses}>
            <div className='LandingPage-title'>Privileged Access</div>
            <div className='LandingPage-description'>
              Connect to your privileged assets so you can launch live sessions
              and check credentials out when you need them.
            </div>
          </section>
          <section className='LandingPage-barPillsAndPage'>
            <div className='LandingPage-barAndPage'>
              <div className='LandingPage-searchBarContainer'>
                <div className='LandingPage-searchBar'>
                  <InputField
                    name='basic'
                    kind='search'
                    placeholder='Search by target system, application, account name, IP, etc.'
                    prefixIcon={
                      // eslint-disable-next-line react/jsx-wrap-multilines
                      <Icon
                        kind='search'
                        color='neutral-600'
                        size='smallMedium'
                      />
                    }
                    value={searchBarValue}
                    setValue={setSearchBarValue}
                  />
                </div>
                <div className='LandingPage-filterButton'>
                  <ButtonSelect
                    label='Filter'
                    isOpen={filterPillVisibility}
                    size='large'
                    prefixIcon={<Icon kind='Filter' color='neutral-600' />}
                    onClick={() =>
                      setFilterPillVisibility(!filterPillVisibility)
                    }
                    BadgeComp={
                      isFilterChecked ? (
                        <Badge kind='dot' color='primary' size='small' />
                      ) : null
                    }
                  />
                </div>
              </div>
              <PaginationCondensed
                count={resArrayLength}
                range={pagRange}
                setRange={setPagRange}
                pageCount={pageCount}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
                className='LandingPage-paginationCondensed'
              />
            </div>
            {filterPillVisibility && (
              <section
                className={`LandingPage-filterPills ${
                  !filterPillVisibility && 'LandingPage-filterPills--isClosed'
                }`}>
                <MenuMulti
                  options={platform.options}
                  label='Platform'
                  trigger={<ButtonSelect kind='pill' label='Platform' />}
                  onChange={platform.setSelected}
                />
                <MenuMulti
                  options={asset.options}
                  label='Asset Type'
                  trigger={<ButtonSelect kind='pill' label='Asset Type' />}
                  onChange={asset.setSelected}
                />
                {/* <MenuMulti
                  options={account.options}
                  label='Account'
                  trigger={<ButtonSelect kind='pill' label='Account' />}
                  onChange={account.setSelected}
                /> */}
                {isFilterChecked ? (
                  <Link
                    href='Filter Clear Button'
                    text='Clear'
                    kind='button'
                    onClick={() => {
                      clearAllFilters();
                    }}
                  />
                ) : null}
              </section>
            )}
          </section>
          <section className={listRowContainerClasses}>
            {renderListOrNoData()}
          </section>
          <div className='LandingPage-paginationGroup'>
            <PaginationGroup
              pageCount={pageCount}
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
            />
          </div>
        </section>
      </div>
      {isSidePanelOpen && (
        <aside className='LandingPage-sidePanelContainer'>
          <LandingPageSidePanel
            data={currentListItem}
            isOpen={isSidePanelOpen}
            onClose={sidePanelOnClose}
            accountOnClick={() => {
              checkoutCredentialNavigation();
            }}
            className='LandingPage-sidePanel'
          />
        </aside>
      )}
    </div>
  );
}

export default LandingPage;
